import React, { useLayoutEffect, useRef } from "react"
import { Loader2, Plus } from "lucide-react"
import type { PaperSession } from "../types"
import type { PaperListPagination } from "../hooks/usePaperSessions"
import SessionCard from "./SessionCard"
import "./SessionList.style.scss"

interface SessionListProps {
  sessions: PaperSession[]
  pagination: PaperListPagination
  requestedPage?: number
  loading?: boolean
  launchingId?: string | null
  pdfLoadingId?: string | null
  onPageChange: (page: number) => void
  onCreate: () => void
  onResume: (id: string) => void | Promise<void>
  onViewAnalytics: (id: string) => void
  onRetake: (session: PaperSession) => void | Promise<void>
  onDownloadPdf: (session: PaperSession) => void
  onDelete: (id: string) => void
}

const SessionList = ({
  sessions,
  pagination,
  requestedPage,
  loading = false,
  launchingId = null,
  pdfLoadingId = null,
  onPageChange,
  onCreate,
  onResume,
  onViewAnalytics,
  onRetake,
  onDownloadPdf,
  onDelete,
}: SessionListProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const pageRef = useRef(pagination.page)
  const isEmpty = !pagination.total && !sessions.length
  const totalPages = Math.max(pagination.total_pages || 1, 1)
  const showPager = pagination.total > pagination.limit
  const pendingPage = requestedPage ?? pagination.page
  const isPaging = pendingPage !== pagination.page
  const fetchingNext = isPaging && pendingPage > pagination.page
  const fetchingPrev = isPaging && pendingPage < pagination.page

  useLayoutEffect(() => {
    if (pageRef.current === pagination.page) return
    pageRef.current = pagination.page
    const scroller = sectionRef.current?.closest(
      ".practice-papers-page__list"
    )
    if (scroller instanceof HTMLElement) {
      scroller.scrollTo({ top: 0, behavior: "auto" })
      return
    }
    sectionRef.current?.scrollIntoView({ behavior: "auto", block: "start" })
  }, [pagination.page])

  return (
    <section
      className="session-list"
      id="my-papers"
      ref={sectionRef}
      aria-busy={isPaging || undefined}
    >
      <div className="session-list__header">
        <h2>My Papers</h2>
        <span>
          {pagination.total} session{pagination.total !== 1 ? "s" : ""}
        </span>
      </div>
      {isEmpty ? (
        <div className="session-list__empty">
          {loading ? (
            <p>Loading papers…</p>
          ) : (
            <>
              <p>No practice papers yet</p>
              <p className="session-list__empty-copy">
                Create one from the question bank to get started.
              </p>
              <button type="button" onClick={onCreate}>
                <Plus size={16} />
                Create paper
              </button>
            </>
          )}
        </div>
      ) : (
        <>
          <div
            className={`session-list__stack${isPaging ? " is-fetching" : ""}`}
            key={pagination.page}
          >
            {sessions.map((session) => (
              <SessionCard
                key={session.id}
                session={session}
                isLaunching={launchingId === session.id}
                pdfLoading={pdfLoadingId === session.id}
                onResume={() => onResume(session.id)}
                onViewAnalytics={() => onViewAnalytics(session.id)}
                onRetake={() => onRetake(session)}
                onDownloadPdf={() => onDownloadPdf(session)}
                onDelete={() => onDelete(session.id)}
              />
            ))}
          </div>
          {showPager && (
            <div className="session-list__pagination">
              <button
                type="button"
                disabled={pagination.page <= 1 || isPaging || loading}
                onClick={() => onPageChange(Math.max(1, pagination.page - 1))}
              >
                {fetchingPrev ? (
                  <>
                    <Loader2 size={14} className="is-spinning" aria-hidden />
                    Loading…
                  </>
                ) : (
                  "Previous"
                )}
              </button>
              <span>
                {isPaging
                  ? `Loading page ${pendingPage}…`
                  : `Page ${pagination.page} of ${totalPages}${
                      pagination.total > 0 ? ` · ${pagination.total} total` : ""
                    }`}
              </span>
              <button
                type="button"
                disabled={
                  pagination.page >= totalPages ||
                  totalPages <= 1 ||
                  isPaging ||
                  loading
                }
                onClick={() => onPageChange(pagination.page + 1)}
              >
                {fetchingNext ? (
                  <>
                    <Loader2 size={14} className="is-spinning" aria-hidden />
                    Loading…
                  </>
                ) : (
                  "Next"
                )}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  )
}

export default SessionList
